import { Activity } from 'lucide-react';
import { unusualVolumeBadge } from '@/lib/volume';
import ChipTip from '@/components/ChipTip';

/**
 * UnusualActivityLine — single-line strip surfacing watchlist tickers
 * whose today-vs-3-month-avg-volume ratio lands in either the green
 * (≥2×) or red (≤0.4×) band.
 *
 * Round 105c (Jun 2026) — chip split: the TICKER opens the Stock
 * Modal directly; the volume PILL opens a ChipTip click-popover with
 * the explainer text (same copy desktop hover shows) + an
 * "Open TICKER →" link. Phone users finally get the explanation.
 */
export default function UnusualActivityLine({ stocks = [], onStockClick }) {
  const sev = { heavy: 0, above: 1, light: 2 };
  const unusual = (stocks || [])
    .map((s) => {
      const badge = unusualVolumeBadge(s?.volume, s?.avg_volume);
      if (!badge) return null;
      return { ticker: s.ticker, badge };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (sev[a.badge.severity] !== sev[b.badge.severity]) {
        return sev[a.badge.severity] - sev[b.badge.severity];
      }
      return Math.abs(b.badge.ratio - 1) - Math.abs(a.badge.ratio - 1);
    })
    .slice(0, 8);

  if (unusual.length === 0) return null;

  return (
    <div
      className="my-3 px-3 py-2 rounded-lg border bg-card flex flex-wrap items-center gap-2"
      data-testid="watchlist-unusual-activity-line"
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
        <Activity className="w-3.5 h-3.5 text-violet-500" />
        Unusual Activity Today
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {unusual.map((u) => {
          const tip = u.badge.severity === 'heavy'
            ? `Heavy activity — ${u.ticker} is trading at ${u.badge.label}. Often a news/earnings/dividend event.`
            : u.badge.severity === 'above'
              ? `${u.ticker} is trading at ${u.badge.label} — above its 3-month average.`
              : `${u.ticker} is trading at ${u.badge.label} — below its 3-month average.`;
          return (
            <span key={u.ticker} className="inline-flex items-center gap-1 px-1 py-[2px] rounded">
              <button
                type="button"
                onClick={() => onStockClick && onStockClick(u.ticker)}
                className="text-[11px] font-bold font-mono px-0.5 rounded hover:bg-muted transition-colors"
                data-testid={`unusual-activity-${u.ticker}`}
                title={`Open ${u.ticker} in the Stock Modal`}
              >
                {u.ticker}
              </button>
              <ChipTip
                text={tip}
                ticker={u.ticker}
                onOpenTicker={onStockClick}
                testId={`unusual-pill-${u.ticker}`}
              >
                <span className={`text-[9px] font-bold px-1 py-[1px] rounded-sm whitespace-nowrap ${u.badge.classes}`}>
                  {u.badge.label}
                </span>
              </ChipTip>
            </span>
          );
        })}
      </div>
    </div>
  );
}
